/**
 * ValoraaBead motion layer (loaded by snippets/vb-theme-styles.liquid, deferred). Pairs with
 * assets/vb-motion.css and does only what CSS cannot:
 *
 * 1. Accordion height fallback. Browsers with `interpolate-size` animate <details> in CSS.
 *    Elsewhere (Safari, Firefox) this animates the <details> height with the Web Animations API,
 *    and the footer's mobile link columns ([hidden] lists) the same way. Native keyboard and
 *    screen reader behaviour is untouched: summaries stay real summaries, `open` stays the state.
 * 2. Lazy images that arrive after the page is shown fade in (class `vb-img-in`).
 * 3. `--vb-scroll-offset` on <html>: the sticky header's height, so in-page anchors and focus
 *    stop below it (scroll-padding in vb-motion.css).
 * 4. `.vb-motion-ready` on <html> after first paint, so nothing animates on page load.
 * 5. Perf: on mouse devices, the hover (second) photo of product cards near the viewport is
 *    switched to eager after `load`, so the first hover never flashes blank.
 * 6. Header (BRAND-VB.md section 7): `data-vb-solid` (white bar) once the page is 100px down,
 *    or while the header is hovered, keyboard-focused or has a menu open; with the header's
 *    Scroll behaviour set to 'hide', `data-vb-hidden` while scrolling down (slides away),
 *    removed on scroll up. Listens to the real scroller: the document on phones,
 *    `.page-wrapper` from 990px up (Horizon).
 *
 * 7. Home sections: headings, story text and tiles rise softly into view once (`vb-reveal`).
 *
 * All of it stands down when the visitor prefers reduced motion.
 */
(() => {
  if (/** @type {any} */ (window).vbMotion) return;
  /** @type {any} */ (window).vbMotion = true;

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 750px)');

  /** Read the shared tokens from CSS so the fallback matches the CSS path exactly. */
  const token = (/** @type {string} */ name, /** @type {string} */ fallback) =>
    getComputedStyle(root).getPropertyValue(name).trim() || fallback;
  const ms = (/** @type {string} */ value) => {
    const n = parseFloat(value);
    return value.endsWith('ms') ? n : n * 1000;
  };

  // `data-vb-motion-fallback` on <html> forces the script path (QA only).
  const cssHeights = CSS.supports('interpolate-size', 'allow-keywords') && !root.hasAttribute('data-vb-motion-fallback');
  if (!cssHeights) root.classList.add('vb-motion-js');

  const motion = () => ({
    open: ms(token('--vb-dur-slow', '300ms')),
    close: ms(token('--vb-dur-base', '240ms')),
    easeOut: token('--vb-ease-out', 'ease-out'),
    easeInOut: token('--vb-ease-in-out', 'ease-in-out'),
  });

  /* ---------- 1. Accordion fallback ---------- */

  const ACCORDION = 'details.vb-faq-item, details.vb-accordion-row, accordion-custom > details';

  /** @type {WeakMap<Element, Animation>} */
  const running = new WeakMap();
  /** @type {WeakMap<Element, Animation>} */
  const fading = new WeakMap();

  /**
   * Horizon marks some accordions as popovers or static on one breakpoint.
   * @param {HTMLDetailsElement} details
   */
  const canAnimate = (details) => {
    const host = details.parentElement;
    if (host?.localName !== 'accordion-custom') return true;
    if (desktop.matches && host.dataset.disableAnimationOnDesktop === 'true') return false;
    if (desktop.matches && host.dataset.disableOnDesktop === 'true') return false;
    if (!desktop.matches && host.dataset.disableOnMobile === 'true') return false;
    return true;
  };

  /**
   * @param {HTMLElement} element
   * @param {Keyframe[]} frames
   * @param {number} duration
   * @param {string} easing
   * @param {() => void} [done]
   */
  const animateHeight = (element, frames, duration, easing, done) => {
    running.get(element)?.cancel();
    element.style.overflow = 'clip';
    const animation = element.animate(frames, { duration, easing });
    running.set(element, animation);
    const finish = () => {
      if (running.get(element) !== animation) return;
      running.delete(element);
      element.style.removeProperty('overflow');
      done?.();
    };
    animation.addEventListener('finish', finish);
    animation.addEventListener('cancel', () => {
      if (running.get(element) === animation) element.style.removeProperty('overflow');
    });
  };

  /**
   * Flips a panel with its own CSS transitions held (.vb-motion-measure), so the height read
   * afterwards is the real end height and no component transition replays behind the script.
   * @param {HTMLDetailsElement} details
   * @param {boolean} open
   */
  const setOpenInstantly = (details, open) => {
    details.classList.add('vb-motion-measure');
    details.open = open;
    const height = details.getBoundingClientRect().height;
    details.classList.remove('vb-motion-measure');
    return height;
  };

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} summary
   */
  const toggleDetails = (details, summary) => {
    const { open, close, easeOut, easeInOut } = motion();
    const start = details.getBoundingClientRect().height;
    const opening = !details.open || details.hasAttribute('data-vb-closing');
    const content = /** @type {HTMLElement | null} */ (details.querySelector(':scope > :not(summary)'));

    if (opening) {
      // Open (or reverse a close that is still running)
      details.removeAttribute('data-vb-closing');
      running.get(details)?.cancel();
      fading.get(details)?.cancel();
      const end = setOpenInstantly(details, true);
      content?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: open, easing: easeOut });
      animateHeight(details, [{ height: `${start}px` }, { height: `${end}px` }], open, easeOut);
      return;
    }

    // Close: [open] stays until the height reaches the summary, then the panel closes for real.
    const styles = getComputedStyle(details);
    const end =
      summary.getBoundingClientRect().height +
      parseFloat(styles.borderBlockStartWidth) +
      parseFloat(styles.borderBlockEndWidth) +
      parseFloat(styles.paddingBlockStart) +
      parseFloat(styles.paddingBlockEnd);
    details.setAttribute('data-vb-closing', '');
    const fade = content?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: close, easing: easeInOut, fill: 'forwards' });
    if (fade) fading.set(details, fade);
    animateHeight(details, [{ height: `${start}px` }, { height: `${end}px` }], close, easeInOut, () => {
      details.removeAttribute('data-vb-closing');
      setOpenInstantly(details, false);
      fade?.cancel();
      fading.delete(details);
    });
  };

  if (!cssHeights) {
    document.addEventListener('click', (event) => {
      if (reducedMotion.matches || event.defaultPrevented || event.button !== 0) return;
      if (!(event.target instanceof Element)) return;

      const summary = event.target.closest('summary');
      const details = summary?.parentElement;
      if (!(summary instanceof HTMLElement) || !(details instanceof HTMLDetailsElement)) return;
      if (details.querySelector(':scope > summary') !== summary) return;
      if (!details.matches(ACCORDION) || !canAnimate(details)) return;
      if (event.target !== summary && event.target.closest('a[href], button, input, select, textarea')) return;

      event.preventDefault();
      toggleDetails(details, summary);
    });

    // Footer link columns: the section toggles [hidden]; CSS keeps a closing list displayed for
    // the length of the animation (transition on display, allow-discrete).
    const footerHeights = new WeakMap();
    const observer = new MutationObserver((records) => {
      if (reducedMotion.matches || desktop.matches || !root.classList.contains('vb-motion-ready')) return;
      const { open, close, easeOut, easeInOut } = motion();

      for (const record of records) {
        const list = /** @type {HTMLElement} */ (record.target);
        if (list.hidden) {
          const from = list.getBoundingClientRect().height || footerHeights.get(list) || 0;
          animateHeight(list, [{ height: `${from}px` }, { height: '0px' }], close, easeInOut);
        } else {
          const to = list.getBoundingClientRect().height;
          footerHeights.set(list, to);
          animateHeight(list, [{ height: '0px' }, { height: `${to}px` }], open, easeOut);
        }
      }
    });
    const watchFooter = () => {
      for (const list of document.querySelectorAll('vb-footer-accordion .vb-footer__links')) {
        observer.observe(list, { attributes: true, attributeFilter: ['hidden'] });
      }
    };
    watchFooter();
    document.addEventListener('shopify:section:load', watchFooter);
  }

  /* ---------- 2. Lazy images fade in ---------- */

  /** @param {HTMLImageElement} image */
  const watchImage = (image) => {
    if (image.complete || image.dataset.vbImg) return;
    image.dataset.vbImg = '';
    image.addEventListener(
      'load',
      () => {
        if (reducedMotion.matches) return;
        image.classList.add('vb-img-in');
        image.addEventListener('animationend', () => image.classList.remove('vb-img-in'), { once: true });
      },
      { once: true }
    );
  };

  /** @param {ParentNode} scope */
  const watchImages = (scope) => {
    for (const image of scope.querySelectorAll('img[loading="lazy"]')) {
      if (image instanceof HTMLImageElement) watchImage(image);
    }
  };

  /* ---------- 5. Warm the card hover image (desktop only) ---------- */

  // The second card photo sits in a horizontal slideshow track, so the browser never lazy-loads
  // it on vertical scroll and the first hover can flash blank. On mouse devices, switch it to
  // eager once its card is in view (after `load`, so it never competes with the LCP).
  // Touch devices and data-saver connections keep it lazy (no hover preview there).
  const connection = /** @type {any} */ (navigator).connection;
  const warmCards =
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !connection?.saveData &&
    !/(^|-)2g$/.test(connection?.effectiveType || '');

  /** @type {IntersectionObserver | null} */
  let cardObserver = null;

  /** @param {Element} gallery */
  const warmCard = (gallery) => {
    const image = gallery.querySelector('slideshow-slide')?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!(image instanceof HTMLImageElement)) return;
    // `sizes="auto, …"` is only valid on lazy images: drop `auto` first, or Chrome falls back to
    // 100vw and fetches the largest file.
    image.sizes = image.sizes.replace(/^\s*auto\s*,\s*/, '');
    image.loading = 'eager';
  };

  /** @param {Element} scope */
  const watchCards = (scope) => {
    if (!cardObserver) return;
    const galleries = scope.matches('.card-gallery') ? [scope] : scope.querySelectorAll('.card-gallery');
    for (const gallery of galleries) {
      if (gallery.querySelector('slideshow-slide + slideshow-slide img[loading="lazy"]')) cardObserver.observe(gallery);
    }
  };

  if (warmCards && 'IntersectionObserver' in window) {
    const startCards = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            warmCard(entry.target);
          }
        },
        { threshold: 0.25 }
      );
      cardObserver = observer;
      watchCards(document.documentElement);
    };
    const idle = /** @type {any} */ (window).requestIdleCallback || ((/** @type {Function} */ fn) => setTimeout(fn, 200));
    if (document.readyState === 'complete') idle(startCards);
    else window.addEventListener('load', () => idle(startCards), { once: true });
  }

  watchImages(document);
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof HTMLImageElement && node.loading === 'lazy') watchImage(node);
        else if (node instanceof Element) {
          watchImages(node);
          watchCards(node);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  /* ---------- 3. Scroll offset under the sticky header ---------- */

  const setScrollOffset = () => {
    const header = document.querySelector('#header-component');
    const sticky = header?.hasAttribute('sticky') && header.getAttribute('sticky') !== 'scroll-up';
    const height = sticky && header ? Math.round(header.getBoundingClientRect().height) : 0;
    root.style.setProperty('--vb-scroll-offset', `${height}px`);
  };

  const header = document.querySelector('#header-component');
  if (header) new ResizeObserver(setScrollOffset).observe(header);
  setScrollOffset();

  /* ---------- 6. Header: transparent at the top, solid below, hides on scroll down ---------- */

  // Header > Scroll behaviour (`data-vb-scroll`): 'hide' (default) slides the bar away while the
  // shopper scrolls down and back, white, on scroll up; 'always' keeps it fixed in view.
  // Both: transparent over the home hero until SOLID_AT px, white after (Raniwala switches at 100).
  /** @param {HTMLElement} header */
  const initHeader = (header) => {
    if (header.dataset.vbHeaderReady) return;
    header.dataset.vbHeaderReady = 'true';
    const wrapper = document.querySelector('.page-wrapper');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const SOLID_AT = 100;
    const DELTA = 6;
    const scrollTop = () =>
      Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, wrapper ? wrapper.scrollTop : 0);
    let last = scrollTop();
    let frame = 0;

    // Something in the header is in use (keyboard focus, pointer over it, a menu or search open):
    // keep it in place and white.
    // The search panel (#search-modal, snippets/search-modal.liquid) is rendered outside the
    // header, so it is checked on its own: the bar turns white while search is open.
    const searchOpen = () => {
      const modal = document.getElementById('search-modal');
      return Boolean(modal && (modal.querySelector('dialog[open]') || modal.contains(document.activeElement)));
    };
    // Keyboard focus inside <shopify-account> lives in its shadow root, so `:focus-visible`
    // on the header misses it: remember that the last interaction was the keyboard instead.
    let keyboard = false;
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' || event.key.startsWith('Arrow')) keyboard = true;
    }, { capture: true, passive: true });
    document.addEventListener('pointerdown', () => { keyboard = false; }, { capture: true, passive: true });
    const keyboardFocus = () => {
      const active = document.activeElement;
      return keyboard && active instanceof Element && header.contains(active) && active !== document.body;
    };
    // The bar only floats over a first section that opts in (`vb-header-overlay`, e.g. vb-hero);
    // otherwise it is solid from the top (sections/header.liquid does the same in CSS).
    const overlay = () => Boolean(document.querySelector('.content-for-layout > .vb-header-overlay:first-child'));
    const engaged = () =>
      Boolean(header.querySelector(':focus-visible')) ||
      keyboardFocus() ||
      (finePointer.matches && header.matches(':hover')) ||
      Boolean(header.querySelector('details[open], [aria-expanded="true"], dialog[open]')) ||
      searchOpen();

    const update = () => {
      frame = 0;
      const top = scrollTop();
      const busy = engaged();
      header.toggleAttribute('data-vb-solid', top > SOLID_AT || busy || !overlay());

      if (header.dataset.vbScroll !== 'hide' || busy || top <= header.offsetHeight) {
        header.removeAttribute('data-vb-hidden');
        last = top;
        return;
      }
      const delta = top - last;
      if (delta > DELTA) {
        header.setAttribute('data-vb-hidden', '');
        last = top;
      } else if (delta < -DELTA) {
        header.removeAttribute('data-vb-hidden');
        last = top;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    // One capture listener catches the document (phones) and .page-wrapper (>= 990px) alike,
    // so it keeps working when the scroller switches on resize.
    document.addEventListener(
      'scroll',
      (event) => {
        const target = event.target;
        if (!header.isConnected) return;
        if (target === document || target === wrapper || target === document.scrollingElement) schedule();
      },
      { capture: true, passive: true }
    );
    for (const type of ['pointerenter', 'pointerleave', 'focusin', 'focusout', 'click', 'keyup']) {
      header.addEventListener(type, () => setTimeout(schedule, 0));
    }
    header.addEventListener('toggle', schedule, true);
    new MutationObserver(schedule).observe(header, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'open'],
    });
    const searchModal = document.getElementById('search-modal');
    if (searchModal) {
      new MutationObserver(schedule).observe(searchModal, { subtree: true, attributes: true, attributeFilter: ['open'] });
      searchModal.addEventListener('close', () => setTimeout(schedule, 0), true);
    }
    // Theme editor: adding, removing or reordering sections can change what sits first.
    for (const type of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(type, () => header.isConnected && setTimeout(schedule, 0));
    }
    update();

    // Fit: from 1200px up the nav sits in one row beside the wordmark. The main menu is admin
    // content, so when its first link would touch the wordmark, `data-vb-compact` switches the
    // bar to the menu-panel layout (sections/header.liquid). Below 1200px CSS alone does that.
    const wide = window.matchMedia('(min-width: 1200px)');
    let fitFrame = 0;
    const fit = () => {
      fitFrame = 0;
      header.removeAttribute('data-vb-compact');
      if (!wide.matches) return;
      const first = header.querySelector('.vb-nav__item');
      const logo = header.querySelector('.header-logo');
      if (!first || !logo) return;
      const room = first.getBoundingClientRect().left - logo.getBoundingClientRect().right;
      header.toggleAttribute('data-vb-compact', room < 24);
    };
    const scheduleFit = () => {
      if (!fitFrame) fitFrame = requestAnimationFrame(fit);
    };
    window.addEventListener('resize', () => header.isConnected && scheduleFit(), { passive: true });
    wide.addEventListener('change', scheduleFit);
    document.fonts?.ready.then(scheduleFit);
    fit();
  };

  if (header instanceof HTMLElement) initHeader(header);
  // Theme editor: the header section re-renders as a new element; bind to it.
  document.addEventListener('shopify:section:load', (event) => {
    const fresh = event.target instanceof Element ? event.target.querySelector('#header-component') : null;
    if (fresh instanceof HTMLElement) initHeader(fresh);
  });

  /* ---------- 3b. Menu drawer accordions: aria-expanded mirrors the open state ---------- */

  // Horizon's header-drawer.js only writes aria-expanded on some paths (Escape sets false,
  // an open never sets true), so screen readers heard a stale state. `toggle` does not bubble,
  // so listen in the capture phase for every <details> inside the drawer.
  const DRAWER_DETAILS = 'header-drawer details';
  /** @param {HTMLDetailsElement} details */
  const syncExpanded = (details) => {
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', String(details.open));
  };
  document.addEventListener(
    'toggle',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLDetailsElement && target.matches(DRAWER_DETAILS)) syncExpanded(target);
    },
    true
  );
  const syncAllDrawers = () =>
    document.querySelectorAll(DRAWER_DETAILS).forEach((d) => syncExpanded(/** @type {HTMLDetailsElement} */ (d)));
  syncAllDrawers();
  document.addEventListener('shopify:section:load', syncAllDrawers);

  /* ---------- 3c. Menu search opens the predictive search panel ---------- */

  // snippets/vb-header-drawer-search.liquid renders a field-like button (hidden until this runs)
  // and a plain GET form fallback. With the search panel on the page, swap them, and on
  // activation close the menu, open #search-modal and put focus in its input, so phones get the
  // same live suggestions and products as the desktop search icon.
  const SEARCH_TRIGGER = '[data-vb-open-search]';
  const enhanceDrawerSearch = () => {
    if (!document.getElementById('search-modal')) return;
    document.querySelectorAll(SEARCH_TRIGGER).forEach((trigger) => trigger.removeAttribute('hidden'));
    document.querySelectorAll('[data-vb-drawer-search-fallback]').forEach((form) => form.setAttribute('hidden', ''));
  };
  enhanceDrawerSearch();
  document.addEventListener('shopify:section:load', enhanceDrawerSearch);

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(SEARCH_TRIGGER) : null;
    const modal = document.getElementById('search-modal');
    if (!trigger || !modal || typeof modal.showDialog !== 'function') return;
    event.preventDefault();

    const drawer = trigger.closest('header-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();

    modal.showDialog();
    // showDialog opens on the next frame; focus the input once the dialog is open
    const input = modal.querySelector('input[type="search"], input.search-input');
    let tries = 0;
    const focusInput = () => {
      const dialog = modal.querySelector('dialog');
      if (dialog?.open && input instanceof HTMLElement) {
        input.focus({ preventScroll: true });
      } else if (tries++ < 20) {
        requestAnimationFrame(focusInput);
      }
    };
    requestAnimationFrame(focusInput);
  });

  /* ---------- 7. Home: blocks rise softly into view ---------- */

  // Section headings, story text and tiles below the fold start 20px low and transparent, and
  // settle as they scroll in (once; nothing hides again). Only opacity and translate change, so
  // nothing reflows. Off with reduced motion, in the theme editor and without IntersectionObserver;
  // anything already on screen (or above it, after a reload mid-page) is never hidden.
  const REVEAL = [
    '.vb-heading',
    '.vb-intro__inner',
    '.vb-statement__quote',
    '.vb-statement__card',
    '.vb-discover__item',
    '.vb-world__media',
    '.vb-world__content',
    '.vb-story__content',
    '.vb-connect__grid',
  ].join(', ');
  const designMode = /** @type {any} */ (window).Shopify?.designMode;

  const setupReveal = () => {
    if (reducedMotion.matches || designMode || !('IntersectionObserver' in window)) return;
    const main = document.querySelector('main');
    if (!main) return;
    const targets = [...main.querySelectorAll(REVEAL)].filter(
      (el) => !el.classList.contains('vb-reveal') && !el.parentElement?.closest(REVEAL)
    );
    if (!targets.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = /** @type {HTMLElement} */ (entry.target);
          el.classList.add('is-revealed');
          observer.unobserve(el);
          // Once settled, hand the element back to its own styles (its hover transitions etc.).
          const done = (/** @type {TransitionEvent} */ event) => {
            if (event.target !== el || event.propertyName !== 'translate') return;
            el.removeEventListener('transitionend', done);
            el.classList.remove('vb-reveal', 'is-revealed');
            el.style.removeProperty('--vb-reveal-delay');
          };
          el.addEventListener('transitionend', done);
        }
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const fold = window.innerHeight;
    for (const el of targets) {
      if (el.getBoundingClientRect().top < fold) continue;
      // Tiles in one row arrive one after another (80ms apart, at most four steps).
      if (el.classList.contains('vb-discover__item')) {
        const index = [...(el.parentElement?.children ?? [])].indexOf(el);
        /** @type {HTMLElement} */ (el).style.setProperty('--vb-reveal-delay', `${(index % 4) * 80}ms`);
      }
      el.classList.add('vb-reveal');
      observer.observe(el);
    }
    // Printing or saving the page: show everything.
    window.addEventListener('beforeprint', () => {
      for (const el of document.querySelectorAll('.vb-reveal')) el.classList.add('is-revealed');
    });
  };
  setupReveal();

  /* ---------- 4. Ready: transitions that must not run on first paint ---------- */

  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('vb-motion-ready')));
})();
